import { useRef, useEffect } from "react";
import { useScrollReveal } from "../useScrollReveal";
import "./Es3Filmstrip.css";

const STRIPS = [
  { src: "/image/es3/v1-singer-a.png",  alt: "A performer takes the stage",            stamp: "EX 01", caption: "a voice fills the room" },
  { src: "/image/es3/v2-guitar-b.png",  alt: "A guitarist deep in session",            stamp: "EX 02", caption: "a session begins" },
  { src: "/image/es3/v3-poetry-a.png",  alt: "A poetry reading in the room",           stamp: "EX 03", caption: "words on a weeknight" },
  { src: "/image/es3/v5-sketch-b.png",  alt: "An artisan at their sketchbook",         stamp: "EX 04", caption: "the sketch that started it" },
  { src: "/image/es3/v6-pod-a.png",     alt: "The pod, a quiet room inside the room",  stamp: "EX 05", caption: "a quiet corner inside a quieter room" },
  { src: "/image/es3/v7-niche-b.png",   alt: "Work displayed in the lit niche wall",   stamp: "EX 06", caption: "made by someone you could meet tonight" },
  { src: "/image/es3/v8-browse-a.png",  alt: "Browsing the shelves",                   stamp: "EX 07", caption: "something catches your eye" },
  { src: "/image/es3/v9-table-a.png",   alt: "Conversation at the long table",         stamp: "EX 08", caption: "slow games, long conversations" },
];

const TRACK = [...STRIPS, ...STRIPS];

function Es3Filmstrip() {
  const { ref: headRef, fromDir } = useScrollReveal(0.1);
  const { ref: outerRef, fromDiagonal } = useScrollReveal(0.08);
  const trackRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    const outer = outerRef.current;
    if (!track || !outer) return;

    const s = {
      x: 0,
      halfWidth: 0,
      dragging: false,
      lastClientX: 0,
      lastClientY: 0,
      rafId: null,
      lastTime: performance.now(),
    };

    function init() {
      s.halfWidth = track.scrollWidth / 2;
      s.lastTime = performance.now();
      s.rafId = requestAnimationFrame(tick);
    }

    function tick(now) {
      const dt = now - s.lastTime;
      s.lastTime = now;

      if (!s.dragging) {
        // auto-scroll speed matched to original 50s animation
        const speed = s.halfWidth > 0 ? s.halfWidth / 50 : 60;
        s.x -= speed * (dt / 1000);
      }

      // infinite wrap
      if (s.halfWidth > 0) {
        while (s.x < -s.halfWidth) s.x += s.halfWidth;
        while (s.x > 0) s.x -= s.halfWidth;
      }

      track.style.transform = `translateX(${s.x}px)`;
      s.rafId = requestAnimationFrame(tick);
    }

    // wait two frames so scrollWidth is painted and accurate
    requestAnimationFrame(() => requestAnimationFrame(init));

    // ── drag helpers ──────────────────────────────────────────
    function startDrag(clientX, clientY) {
      s.dragging = true;
      s.lastClientX = clientX;
      s.lastClientY = clientY;
      outer.style.cursor = "grabbing";
    }

    function moveDrag(clientX) {
      if (!s.dragging) return;
      const dx = clientX - s.lastClientX;
      s.x += dx;
      s.lastClientX = clientX;
    }

    function endDrag() {
      if (!s.dragging) return;
      s.dragging = false;
      outer.style.cursor = "";
    }

    // ── mouse (document-level so fast drags don't break) ─────
    function onMouseDown(e) {
      e.preventDefault();
      startDrag(e.clientX, e.clientY);
      function onMove(e) { moveDrag(e.clientX); }
      function onUp() {
        endDrag();
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      }
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    }

    // ── touch ─────────────────────────────────────────────────
    function onTouchStart(e) {
      startDrag(e.touches[0].clientX, e.touches[0].clientY);
    }

    function onTouchMove(e) {
      if (!s.dragging) return;
      const dx = Math.abs(e.touches[0].clientX - s.lastClientX);
      const dy = Math.abs(e.touches[0].clientY - s.lastClientY);
      if (dx > dy) {
        e.preventDefault();
        moveDrag(e.touches[0].clientX);
      }
    }

    outer.addEventListener("mousedown", onMouseDown);
    outer.addEventListener("touchstart", onTouchStart, { passive: true });
    outer.addEventListener("touchmove", onTouchMove, { passive: false });
    outer.addEventListener("touchend", endDrag, { passive: true });

    return () => {
      if (s.rafId) cancelAnimationFrame(s.rafId);
      outer.removeEventListener("mousedown", onMouseDown);
      outer.removeEventListener("touchstart", onTouchStart);
      outer.removeEventListener("touchmove", onTouchMove);
      outer.removeEventListener("touchend", endDrag);
    };
  }, [outerRef]);

  return (
    <div className="es3-filmstrip-wrap" id="es3-film">
      <div className="es3-filmstrip-head" ref={headRef} style={fromDir(0)}>
        <div className="es3-hand">the preview</div>
        <h2 className="es3-h2">Not open yet. <em>But this is how it will feel.</em></h2>
      </div>

      <div
        className="es3-filmstrip-outer"
        ref={outerRef}
        style={fromDiagonal(80)}
      >
        <div className="es3-filmstrip-track" ref={trackRef}>
          {TRACK.map((s, i) => (
            <figure key={i} className="es3-strip-figure">
              <div className="es3-photo es3-strip-photo">
                <img src={s.src} alt={s.alt} loading="lazy" draggable={false} />
                <span className="es3-stamp">{s.stamp}</span>
              </div>
              <div className="es3-caption">{s.caption}</div>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Es3Filmstrip;
